import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../common/Loader";
import {  fetchCentersDetails } from "../../redux/actions/grievanceActions1";
import { useLocation } from "react-router-dom";

const CenterDetails = () => {
  const dispatch = useDispatch();
  const {  loading: centersLoading } = useSelector(
    (state) => state.grievances
  );
  const location = useLocation();
  const { id } = location.state;

  const [centerDetails, setCenterDetails] = useState([]);

  const fetchCenter = async () => {
    try {
      const centerDetails = await dispatch(fetchCentersDetails(id));
      setCenterDetails(Array.isArray(centerDetails) ? centerDetails : []);
    } catch (error) {
      console.error("Error fetching center details:", error);
      setCenterDetails([]);
    }
  };

  useEffect(() => {
    fetchCenter();
  }, [dispatch]);

  if (centersLoading && !centerDetails.length) {
    return <Loader message="Loading center details..." />;
  }

  // Handle the case where centerDetails might be empty or not properly loaded
  const centerData = centerDetails && centerDetails.length > 0 ? centerDetails[0] : {};

  // Simple color theme
  const themeColor = "#5e72e4"; // Nice blue color
  const secondaryColor = "#11cdef"; // Secondary teal color

  // Helper function to display "N/A" for empty values
  const displayValue = (value) => {
    if (value === undefined || value === null || value === "" || value === "N/A" || typeof value === 'object') {
      return "N/A";
    }
    return value;
  };

  // Helper function to display date in a clean format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return dateString.split(' ')[0] || "N/A";
    } catch (error) {
      return "N/A";
    }
  };

  const renderDefaultImage = (isPresident) => {
    const color = isPresident ? themeColor : secondaryColor;
    
    return (
      <div 
        className="rounded-circle d-flex align-items-center justify-content-center" 
        style={{ 
          width: "100px", 
          height: "100px", 
          backgroundColor: color,
          margin: "0 auto" 
        }}
      >
        <i className={`fa fa-user-${isPresident ? 'tie' : 'edit'} fa-2x text-white`}></i>
      </div>
    );
  };

  // Check if the centerData object is empty
  const isCenterDataEmpty = !centerData || Object.keys(centerData).length === 0;

  return (
    <div className="container-fluid py-4">
      {/* Back button */}
      <div className="mb-4">
        <button 
          onClick={() => window.history.back()} 
          className="btn btn-sm btn-outline-secondary"
          style={{ borderRadius: "4px" }}
        >
          <i className="fa fa-arrow-left me-2"></i>
          Back
        </button>
      </div>

      {isCenterDataEmpty ? (
        <div className="alert alert-info">
          <i className="fa fa-info-circle me-2"></i>
          No center data available. Please try again later.
        </div>
      ) : (
        <>
          {/* Center Header Card */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow-sm" style={{ borderRadius: "8px", border: "none" }}>
                <div className="card-header" style={{ backgroundColor: themeColor, color: "white", borderRadius: "8px 8px 0 0" }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-white p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                      <i className="fa fa-building" style={{ color: themeColor }}></i>
                    </div>
                    <div>
                      <h4 className="mb-0">Center Information</h4>
                      <p className="mb-0 small">ARESA Center Details</p>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                          width: "40px", 
                          height: "40px", 
                          backgroundColor: `${themeColor}20` 
                        }}>
                          <i className="fa fa-id-badge" style={{ color: themeColor }}></i>
                        </div>
                        <div>
                          <div className="text-muted small">Center ID</div>
                          <div className="fw-bold">{displayValue(centerData.UNIT_CENTRE_ID)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                          width: "40px", 
                          height: "40px", 
                          backgroundColor: `${themeColor}20` 
                        }}>
                          <i className="fa fa-map-marked-alt" style={{ color: themeColor }}></i>
                        </div>
                        <div>
                          <div className="text-muted small">Name & Address</div>
                          <div className="fw-bold">{displayValue(centerData.ADDRESS)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* President Card */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100" style={{ borderRadius: "8px", border: "none" }}>
                <div className="card-header" style={{ backgroundColor: themeColor, color: "white", borderRadius: "8px 8px 0 0" }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-white p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px" }}>
                      <i className="fa fa-user-tie" style={{ color: themeColor }}></i>
                    </div>
                    <div>
                      <h5 className="mb-0">President</h5>
                      <p className="mb-0 small">Center Leadership</p>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="text-center mb-4">
                    {/* President Photo */}
                    {centerData.PRE_PHOTO ? (
                      <div className="mb-3" style={{ width: "100px", height: "100px", margin: "0 auto" }}>
                        <img 
                          src={centerData.PRE_PHOTO} 
                          alt={displayValue(centerData.PRE_NAME)} 
                          className="rounded-circle object-fit-cover" 
                          style={{ width: "100%", height: "100%", border: "3px solid #eaecf4" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      renderDefaultImage(true)
                    )}
                    
                    <h5 className="mt-3 mb-1 fw-bold">{displayValue(centerData.PRE_NAME)}</h5>
                    <span className="badge" style={{ 
                      backgroundColor: `${themeColor}20`, 
                      color: themeColor, 
                      padding: "6px 12px" 
                    }}>
                      {displayValue(centerData.PRE_RANK_ID)}
                    </span>
                  </div>
                  
                  <div className="info-list">
                    <div className="info-item d-flex align-items-center py-3 border-bottom">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${themeColor}10` 
                      }}>
                        <i className="fa fa-phone" style={{ color: themeColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Mobile Number</div>
                        <div className="fw-medium">{displayValue(centerData.PRE_MOBILE_NO)}</div>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex align-items-center py-3 border-bottom">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${themeColor}10` 
                      }}>
                        <i className="fa fa-calendar-check" style={{ color: themeColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">President Since</div>
                        <div className="fw-medium">{formatDate(centerData.PRE_WEF_DATE)}</div>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex align-items-center py-3 border-bottom">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${themeColor}10` 
                      }}>
                        <i className="fa fa-shield-alt" style={{ color: themeColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Last Unit Served</div>
                        <div className="fw-medium">{displayValue(centerData.PRE_LAST_UNIT_SERVED)}</div>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex align-items-center py-3">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${themeColor}10` 
                      }}>
                        <i className="fa fa-tools" style={{ color: themeColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Trade</div>
                        <div className="fw-medium">{displayValue(centerData.PRE_TRADE_NAME)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secretary Card */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100" style={{ borderRadius: "8px", border: "none" }}>
                <div className="card-header" style={{ backgroundColor: secondaryColor, color: "white", borderRadius: "8px 8px 0 0" }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-white p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px" }}>
                      <i className="fa fa-user-edit" style={{ color: secondaryColor }}></i>
                    </div>
                    <div>
                      <h5 className="mb-0">Secretary</h5>
                      <p className="mb-0 small">Center Administration</p>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="text-center mb-4">
                    {/* Secretary Photo */}
                    {centerData.SEC_PHOTO ? (
                      <div className="mb-3" style={{ width: "100px", height: "100px", margin: "0 auto" }}>
                        <img 
                          src={centerData.SEC_PHOTO} 
                          alt={displayValue(centerData.SEC_NAME)} 
                          className="rounded-circle object-fit-cover" 
                          style={{ width: "100%", height: "100%", border: "3px solid #eaecf4" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      renderDefaultImage(false)
                    )}
                    
                    <h5 className="mt-3 mb-1 fw-bold">{displayValue(centerData.SEC_NAME)}</h5>
                    <span className="badge" style={{ 
                      backgroundColor: `${secondaryColor}20`, 
                      color: secondaryColor, 
                      padding: "6px 12px" 
                    }}>
                      {displayValue(centerData.SEC_RANK_ID)}
                    </span>
                  </div>
                  
                  <div className="info-list">
                    <div className="info-item d-flex align-items-center py-3 border-bottom">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${secondaryColor}10` 
                      }}>
                        <i className="fa fa-phone" style={{ color: secondaryColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Mobile Number</div>
                        <div className="fw-medium">{displayValue(centerData.SEC_MOBILE_NO)}</div>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex align-items-center py-3 border-bottom">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${secondaryColor}10` 
                      }}>
                        <i className="fa fa-calendar-check" style={{ color: secondaryColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Secretary Since</div>
                        <div className="fw-medium">{formatDate(centerData.SEC_WEF_DATE)}</div>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex align-items-center py-3 border-bottom">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${secondaryColor}10` 
                      }}>
                        <i className="fa fa-shield-alt" style={{ color: secondaryColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Last Unit Served</div>
                        <div className="fw-medium">{displayValue(centerData.SEC_LAST_UNIT_SERVED)}</div>
                      </div>
                    </div>
                    
                    <div className="info-item d-flex align-items-center py-3">
                      <div className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: "40px", 
                        height: "40px", 
                        backgroundColor: `${secondaryColor}10` 
                      }}>
                        <i className="fa fa-tools" style={{ color: secondaryColor }}></i>
                      </div>
                      <div>
                        <div className="text-muted small">Trade</div>
                        <div className="fw-medium">{displayValue(centerData.SEC_TRADE_NAME)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CenterDetails;